(function () {
  'use strict';

  var SCRIPT_PROMISE;
  var EPSILON = 0.001;
  var RATIO_TOLERANCE = 0.06;
  var FORMAT_REGISTRY = window.CARealityFormats = window.CARealityFormats || {};

  function clamp(value, min, max) { return Math.max(min, Math.min(max, value)); }
  function lerp(a, b, amount) { return a + (b - a) * amount; }
  function bool(value) { return String(value) === 'true'; }

  function emit(name, detail) {
    var payload = Object.assign({ event: name }, detail || {});
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent(name, { detail: payload }));
  }

  function loadThree(url) {
    if (window.CARealityTHREE) return Promise.resolve(window.CARealityTHREE);
    if (SCRIPT_PROMISE) return SCRIPT_PROMISE;
    SCRIPT_PROMISE = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-ca-reality-three]');
      var script = existing || document.createElement('script');
      var previousThree = window.THREE;
      var deadline = window.setTimeout(function () { reject(new Error('Three.js load timed out')); }, 12000);
      function ready() {
        window.clearTimeout(deadline);
        var runtime = window.THREE;
        if (!runtime || String(runtime.REVISION) !== '134') return reject(new Error('Reality Viewer requires pinned Three.js r134'));
        window.CARealityTHREE = runtime;
        if (previousThree) window.THREE = previousThree;
        else try { delete window.THREE; } catch (error) { window.THREE = undefined; }
        resolve(runtime);
      }
      script.addEventListener('load', ready, { once: true });
      script.addEventListener('error', function () { reject(new Error('Three.js failed to load')); }, { once: true });
      if (!existing) {
        script.src = url;
        script.async = true;
        script.dataset.caRealityThree = '';
        document.head.appendChild(script);
      }
    });
    return SCRIPT_PROMISE;
  }

  function hasWebGL() {
    try {
      var canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (canvas.getContext('webgl2') || canvas.getContext('webgl')));
    } catch (error) { return false; }
  }

  function parseDimensions(value) {
    return window.CADimensions ? window.CADimensions.parse(value) : null;
  }

  function currentFormat(scope) {
    scope = scope || document;
    var selected = scope.querySelector('[data-ptv]:checked');
    var fixed = scope.querySelector('[data-pt-fixed]');
    var value = selected ? selected.value : (fixed ? fixed.getAttribute('data-pt-fixed') : '');
    value = String(value || '').toLowerCase();
    if (value.indexOf('canvas') > -1) return 'canvas';
    if (value.indexOf('acrylic') > -1) return 'acrylic';
    if (value.indexOf('poster') > -1 || value.indexOf('paper') > -1) return 'poster';
    if (/digital|download|file/.test(value)) return 'digital';
    return 'framed';
  }

  function ProductDataAdapter(root) {
    this.root = root;
    this.onSelection = this.onSelection.bind(this);
    this.onResolved = this.onResolved.bind(this);
  }
  ProductDataAdapter.prototype.connect = function (callback) {
    this.callback = callback;
    document.addEventListener('change', this.onSelection);
    document.addEventListener('ca:variant-resolved', this.onResolved);
    window.addEventListener('popstate', this.onSelection);
    this.onSelection();
  };
  ProductDataAdapter.prototype.onResolved = function (event) {
    if (!event.detail || String(event.detail.sectionId) !== String(this.root.dataset.sectionId)) return;
    this.onSelection(null, event.detail);
  };
  ProductDataAdapter.prototype.onSelection = function (event, resolved) {
    if (event && !event.target.matches('[data-ptv], [data-sz] input, .sp-vr')) return;
    var scope = this.root.closest('.specimen-section') || document;
    var selectedSize = scope.querySelector('[data-sz] input:checked');
    if (this.callback) this.callback({ format: currentFormat(scope), dimensions: parseDimensions(selectedSize && selectedSize.value, this.root.dataset.defaultUnit), variant: resolved && resolved.variant, available: resolved && resolved.available });
  };
  ProductDataAdapter.prototype.dispose = function () { document.removeEventListener('change', this.onSelection); document.removeEventListener('ca:variant-resolved', this.onResolved); window.removeEventListener('popstate', this.onSelection); };

  function ArtworkResolver(root) {
    this.normalUrl = root.dataset.artworkUrl || '';
    this.highUrl = root.dataset.artworkHighUrl || this.normalUrl;
    this.source = root.dataset.artworkSource || 'unknown';
    this.aspect = clamp(Number(root.dataset.aspectRatio) || 1, 0.15, 7);
  }

  function PerformanceManager() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.constrained = !!((connection && connection.saveData) || (navigator.deviceMemory && navigator.deviceMemory <= 2));
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, this.constrained ? 1.25 : 1.75);
  }

  function TextureManager(THREE, renderer) {
    this.THREE = THREE;
    this.renderer = renderer;
    this.texture = null;
    this.url = '';
    this.generation = 0;
  }
  TextureManager.prototype.load = function (url) {
    var self = this;
    var generation = ++this.generation;
    if (!url) return Promise.reject(new Error('No artwork texture URL'));
    if (this.url === url && this.texture) return Promise.resolve(this.texture);
    return new Promise(function (resolve, reject) {
      var deadline = window.setTimeout(function () { reject(new Error('Artwork texture timed out')); }, 15000);
      new self.THREE.TextureLoader().load(url, function (texture) {
        window.clearTimeout(deadline);
        if (generation !== self.generation) { texture.dispose(); return reject(new Error('Stale artwork texture')); }
        texture.anisotropy = Math.min(8, self.renderer.capabilities.getMaxAnisotropy());
        if ('colorSpace' in texture && self.THREE.SRGBColorSpace) texture.colorSpace = self.THREE.SRGBColorSpace;
        else if ('encoding' in texture && self.THREE.sRGBEncoding) texture.encoding = self.THREE.sRGBEncoding;
        texture.minFilter = self.THREE.LinearMipmapLinearFilter;
        var old = self.texture;
        self.texture = texture;
        self.url = url;
        if (old) old.dispose();
        resolve(texture);
      }, undefined, function (error) { window.clearTimeout(deadline); reject(error); });
    });
  };
  TextureManager.prototype.dispose = function () { this.generation += 1; if (this.texture) this.texture.dispose(); this.texture = null; };

  function PhysicalFormat(THREE) {
    this.THREE = THREE;
    this.group = new THREE.Group();
    this.owned = [];
  }
  PhysicalFormat.prototype.track = function (resource) { this.owned.push(resource); return resource; };
  PhysicalFormat.prototype.applyArtwork = function () {};
  PhysicalFormat.prototype.updateDimensions = function () {};
  PhysicalFormat.prototype.setQuality = function () {};
  PhysicalFormat.prototype.dispose = function () {
    this.owned.forEach(function (resource) { if (resource && resource.dispose) resource.dispose(); });
    this.owned = [];
  };

  function roundedShape(THREE, width, height, radius) {
    var x = -width / 2, y = -height / 2;
    var shape = new THREE.Shape();
    shape.moveTo(x + radius, y);
    shape.lineTo(x + width - radius, y);
    shape.quadraticCurveTo(x + width, y, x + width, y + radius);
    shape.lineTo(x + width, y + height - radius);
    shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    shape.lineTo(x + radius, y + height);
    shape.quadraticCurveTo(x, y + height, x, y + height - radius);
    shape.lineTo(x, y + radius);
    shape.quadraticCurveTo(x, y, x + radius, y);
    return shape;
  }

  function makeWeaveNormal(THREE) {
    var canvas = document.createElement('canvas');
    canvas.width = canvas.height = 128;
    var context = canvas.getContext('2d');
    var image = context.createImageData(128, 128);
    for (var y = 0; y < 128; y += 1) {
      for (var x = 0; x < 128; x += 1) {
        var i = (y * 128 + x) * 4;
        var nx = 128 + Math.sin(x * Math.PI * .72) * 9 + Math.sin(y * Math.PI * .19) * 2;
        var ny = 128 + Math.sin(y * Math.PI * .68) * 9 + Math.sin(x * Math.PI * .17) * 2;
        image.data[i] = nx; image.data[i + 1] = ny; image.data[i + 2] = 246; image.data[i + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);
    var texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(18, 18);
    return texture;
  }

  function CanvasFormat(THREE, config) {
    PhysicalFormat.call(this, THREE);
    this.config = config;
    this.aspect = config.aspect;
    this.dimensions = null;
    this.artworkTexture = null;
    this.build();
  }
  CanvasFormat.prototype = Object.create(PhysicalFormat.prototype);
  CanvasFormat.prototype.constructor = CanvasFormat;
  CanvasFormat.prototype.clear = function () {
    while (this.group.children.length) this.group.remove(this.group.children[0]);
    this.dispose();
  };
  CanvasFormat.prototype.build = function () {
    var T = this.THREE;
    var width = 3.2;
    var height = width / this.aspect;
    if (height > 4.15) { height = 4.15; width = height * this.aspect; }
    var physical = this.dimensions || { widthIn: this.aspect * 24, heightIn: 24 };
    var unitScale = width / physical.widthIn;
    var depth = clamp(this.config.depthIn * unitScale, .055, .42);
    var railWidth = clamp(1.25 * unitScale, .065, Math.min(width, height) * .16);
    var railDepth = depth * .58;
    this.model = { width: width, height: height, depth: depth };
    var white = this.track(new T.MeshStandardMaterial({ color: 0xf6f4ee, roughness: .92, metalness: 0 }));
    var rearFabric = this.track(new T.MeshStandardMaterial({ color: 0xd8d2c7, roughness: .98, metalness: 0 }));
    var wood = this.track(new T.MeshStandardMaterial({ color: 0xb78959, roughness: .82, metalness: 0 }));
    var self = this;
    function box(w, h, d, x, y, z, material) {
      var mesh = new T.Mesh(self.track(new T.BoxGeometry(w, h, d)), material);
      mesh.position.set(x || 0, y || 0, z || 0); mesh.castShadow = true; mesh.receiveShadow = true; self.group.add(mesh);
    }
    var lip = Math.min(width, height) * .012;
    box(width, lip, depth, 0, height / 2 - lip / 2, 0, white);
    box(width, lip, depth, 0, -height / 2 + lip / 2, 0, white);
    box(lip, height - lip * 2, depth, -width / 2 + lip / 2, 0, 0, white);
    box(lip, height - lip * 2, depth, width / 2 - lip / 2, 0, 0, white);

    this.weave = this.track(makeWeaveNormal(T));
    this.weave.repeat.set(physical.widthIn * 3.5, physical.heightIn * 3.5);
    this.frontMaterial = this.track(new T.MeshStandardMaterial({ color: 0xffffff, roughness: .93, metalness: 0, normalMap: this.weave, normalScale: new T.Vector2(.035, .035) }));
    var frontGeometry = this.track(new T.PlaneGeometry(width, height, 1, 1));
    this.front = new T.Mesh(frontGeometry, this.frontMaterial);
    /* Keep the art plane clearly in front of the wrapped edge geometry.
       The former 1px-equivalent offset could z-fight on mobile GPUs. */
    this.front.position.z = depth / 2 + .006;
    this.front.castShadow = true;
    this.group.add(this.front);

    var returnWidth = Math.max(lip * 1.8, railWidth * .52), backZ = -depth / 2 + .006;
    box(width - lip * 2, returnWidth, .012, 0, height / 2 - returnWidth / 2 - lip, backZ, rearFabric);
    box(width - lip * 2, returnWidth, .012, 0, -height / 2 + returnWidth / 2 + lip, backZ, rearFabric);
    box(returnWidth, height - (returnWidth + lip) * 2, .012, -width / 2 + returnWidth / 2 + lip, 0, backZ, rearFabric);
    box(returnWidth, height - (returnWidth + lip) * 2, .012, width / 2 - returnWidth / 2 - lip, 0, backZ, rearFabric);
    var railZ = -depth / 2 + railDepth / 2;
    box(width - lip * 2, railWidth, railDepth, 0, height / 2 - railWidth / 2 - lip, railZ, wood);
    box(width - lip * 2, railWidth, railDepth, 0, -height / 2 + railWidth / 2 + lip, railZ, wood);
    box(railWidth, height - railWidth * 2 - lip * 2, railDepth, -width / 2 + railWidth / 2 + lip, 0, railZ, wood);
    box(railWidth, height - railWidth * 2 - lip * 2, railDepth, width / 2 - railWidth / 2 - lip, 0, railZ, wood);
    if (this.artworkTexture) this.applyArtwork(this.artworkTexture);
  };
  CanvasFormat.prototype.applyArtwork = function (texture) {
    this.artworkTexture = texture;
    this.frontMaterial.map = texture;
    this.frontMaterial.needsUpdate = true;
  };
  CanvasFormat.prototype.updateDimensions = function (dimensions) {
    this.dimensions = dimensions;
    this.clear();
    this.build();
  };
  CanvasFormat.prototype.setQuality = function (quality) {
    if (this.frontMaterial) this.frontMaterial.normalScale.setScalar(quality === 'high' ? .05 : .035);
  };
  FORMAT_REGISTRY.canvas = CanvasFormat;

  function fittedObjectSize(aspect) {
    var width = 3.2, height = width / aspect;
    if (height > 4.15) { height = 4.15; width = height * aspect; }
    return { width: width, height: height };
  }
  function resetPhysicalFormat(format) {
    while (format.group.children.length) format.group.remove(format.group.children[0]);
    format.dispose();
  }
  function addBox(format, width, height, depth, x, y, z, material) {
    var mesh = new format.THREE.Mesh(format.track(new format.THREE.BoxGeometry(width, height, depth)), material);
    mesh.position.set(x || 0, y || 0, z || 0);
    mesh.castShadow = true; mesh.receiveShadow = true; format.group.add(mesh); return mesh;
  }

  function FramedFormat(THREE, config) {
    PhysicalFormat.call(this, THREE); this.config = config; this.aspect = config.aspect; this.dimensions = null; this.artworkTexture = null; this.build();
  }
  FramedFormat.prototype = Object.create(PhysicalFormat.prototype);
  FramedFormat.prototype.constructor = FramedFormat;
  FramedFormat.prototype.build = function () {
    var T = this.THREE, size = fittedObjectSize(this.aspect), w = size.width, h = size.height;
    var frame = Math.max(.075, Math.min(w, h) * .055), depth = .13;
    this.model = { width:w + frame * 2, height:h + frame * 2, depth:depth };
    var black = this.track(new T.MeshStandardMaterial({ color:0x111211, roughness:.58, metalness:.04 }));
    var backing = this.track(new T.MeshStandardMaterial({ color:0x373734, roughness:.9, metalness:0 }));
    addBox(this,w + frame * 2,frame,depth,0,h / 2 + frame / 2,0,black);
    addBox(this,w + frame * 2,frame,depth,0,-h / 2 - frame / 2,0,black);
    addBox(this,frame,h,depth,-w / 2 - frame / 2,0,0,black);
    addBox(this,frame,h,depth,w / 2 + frame / 2,0,0,black);
    addBox(this,w,h,.025,0,0,-depth / 2 + .014,backing);
    this.artMaterial = this.track(new T.MeshStandardMaterial({ color:0xffffff, roughness:.82, metalness:0 }));
    this.art = new T.Mesh(this.track(new T.PlaneGeometry(w,h)),this.artMaterial); this.art.position.z = depth / 2 + .008; this.group.add(this.art);
    var glazeMaterial = this.track(new T.MeshPhysicalMaterial({ color:0xffffff, transparent:true, opacity:.075, roughness:.18, metalness:0, clearcoat:.28, clearcoatRoughness:.2, depthWrite:false }));
    var glaze = new T.Mesh(this.track(new T.PlaneGeometry(w,h)),glazeMaterial); glaze.position.z = depth / 2 + .014; glaze.renderOrder = 2; this.group.add(glaze);
    if (this.artworkTexture) this.applyArtwork(this.artworkTexture);
  };
  FramedFormat.prototype.applyArtwork = function (texture) { this.artworkTexture=texture; this.artMaterial.map=texture; this.artMaterial.needsUpdate=true; };
  FramedFormat.prototype.updateDimensions = function (dimensions) { this.dimensions=dimensions; resetPhysicalFormat(this); this.build(); };
  FORMAT_REGISTRY.framed = FramedFormat;

  function AcrylicFormat(THREE, config) {
    PhysicalFormat.call(this, THREE); this.config=config; this.aspect=config.aspect; this.dimensions=null; this.artworkTexture=null; this.build();
  }
  AcrylicFormat.prototype = Object.create(PhysicalFormat.prototype);
  AcrylicFormat.prototype.constructor = AcrylicFormat;
  AcrylicFormat.prototype.build = function () {
    var T=this.THREE,size=fittedObjectSize(this.aspect),w=size.width,h=size.height,depth=.11;
    this.model={width:w,height:h,depth:depth};
    var rear=this.track(new T.MeshStandardMaterial({color:0x353b3b,roughness:.72,metalness:.32}));
    addBox(this,w,h,.035,0,0,-depth / 2,rear);
    var edge=this.track(new T.MeshPhysicalMaterial({color:0xdde9e7,transparent:true,opacity:.55,roughness:.08,metalness:0,clearcoat:.8,clearcoatRoughness:.08,depthWrite:true}));
    var lip=.022;
    addBox(this,w,lip,depth,0,h/2-lip/2,0,edge); addBox(this,w,lip,depth,0,-h/2+lip/2,0,edge);
    addBox(this,lip,h-lip*2,depth,-w/2+lip/2,0,0,edge); addBox(this,lip,h-lip*2,depth,w/2-lip/2,0,0,edge);
    this.artMaterial=this.track(new T.MeshStandardMaterial({color:0xffffff,roughness:.42,metalness:0}));
    this.art=new T.Mesh(this.track(new T.PlaneGeometry(w-lip*2,h-lip*2)),this.artMaterial); this.art.position.z=.012; this.group.add(this.art);
    var faceMaterial=this.track(new T.MeshPhysicalMaterial({color:0xffffff,transparent:true,opacity:.11,roughness:.06,metalness:0,clearcoat:1,clearcoatRoughness:.04,depthWrite:false}));
    var face=new T.Mesh(this.track(new T.PlaneGeometry(w,h)),faceMaterial); face.position.z=depth/2+.008; face.renderOrder=3; this.group.add(face);
    if(this.artworkTexture)this.applyArtwork(this.artworkTexture);
  };
  AcrylicFormat.prototype.applyArtwork=function(texture){this.artworkTexture=texture;this.artMaterial.map=texture;this.artMaterial.needsUpdate=true;};
  AcrylicFormat.prototype.updateDimensions=function(dimensions){this.dimensions=dimensions;resetPhysicalFormat(this);this.build();};
  FORMAT_REGISTRY.acrylic = AcrylicFormat;

  function Renderer(THREE, performance) {
    this.THREE = THREE;
    this.performance = performance;
    this.scene = new THREE.Scene();
    this.scene.background = null;
    this.camera = new THREE.PerspectiveCamera(30, 1, .05, 100);
    this.camera.position.set(0, 0, 6.2);
    this.webgl = new THREE.WebGLRenderer({ antialias: !performance.constrained, alpha: true, powerPreference: 'high-performance' });
    this.webgl.setClearColor(0xeae8e1, 0);
    this.webgl.setPixelRatio(performance.pixelRatio);
    this.webgl.outputEncoding = THREE.sRGBEncoding || this.webgl.outputEncoding;
    this.webgl.shadowMap.enabled = !performance.constrained;
    this.webgl.shadowMap.type = THREE.PCFSoftShadowMap;
    this.object = new THREE.Group();
    this.scene.add(this.object);
    this.scene.add(new THREE.HemisphereLight(0xf7f5ef, 0x8e8b84, .82));
    var key = new THREE.DirectionalLight(0xfffdf8, .92);
    key.position.set(-3.5, 4.5, 6);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);
    var rim = new THREE.DirectionalLight(0xf3eee4, .26);
    rim.position.set(4, 1, -4);
    this.scene.add(rim);
    this.current = { x: -.035, y: -.075, distance: 6.2 };
    this.target = { x: -.035, y: -.075, distance: 6.2 };
    this.raf = 0;
    this.render = this.render.bind(this);
    this.contextLost = function (event) { event.preventDefault(); if (this.onContextLost) this.onContextLost(); }.bind(this);
    this.webgl.domElement.addEventListener('webglcontextlost', this.contextLost, false);
  }
  Renderer.prototype.setFormat = function (format) {
    while (this.object.children.length) this.object.remove(this.object.children[0]);
    this.format = format;
    this.object.add(format.group);
    this.requestRender();
  };
  Renderer.prototype.mount = function (element) {
    if (this.webgl.domElement.parentElement !== element) element.appendChild(this.webgl.domElement);
    this.mountElement = element;
    this.resize();
  };
  Renderer.prototype.resize = function () {
    if (!this.mountElement) return;
    var rect = this.mountElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    this.camera.aspect = rect.width / rect.height;
    this.camera.updateProjectionMatrix();
    this.webgl.setSize(rect.width, rect.height, false);
    if (this.format && !this.fullView) this.setView('front', true);
    this.requestRender();
  };
  Renderer.prototype.fitDistance = function (margin) {
    if (!this.format) return 6;
    var model = this.format.model, fov = this.camera.fov * Math.PI / 180;
    var vertical = (model.height * (margin || 1.18) / 2) / Math.tan(fov / 2);
    var horizontal = (model.width * (margin || 1.18) / 2) / (Math.tan(fov / 2) * this.camera.aspect);
    return Math.max(vertical, horizontal) + model.depth * 1.5;
  };
  Renderer.prototype.requestRender = function () {
    if (this.visible === false || document.hidden || this.raf) return;
    this.raf = requestAnimationFrame(this.render);
  };
  Renderer.prototype.render = function (time) {
    this.raf = 0;
    var elapsed = Math.min(.05, Math.max(.001, (time - (this.lastTime || time - 16)) / 1000));
    this.lastTime = time;
    var amount = this.performance.reducedMotion ? 1 : 1 - Math.exp(-8 * elapsed);
    this.current.x = lerp(this.current.x, this.target.x, amount);
    this.current.y = lerp(this.current.y, this.target.y, amount);
    this.current.distance = lerp(this.current.distance, this.target.distance, amount);
    this.object.rotation.x = this.current.x;
    this.object.rotation.y = this.current.y;
    this.camera.position.z = this.current.distance;
    this.webgl.render(this.scene, this.camera);
    if (Math.abs(this.current.x - this.target.x) > EPSILON || Math.abs(this.current.y - this.target.y) > EPSILON || Math.abs(this.current.distance - this.target.distance) > EPSILON) this.requestRender();
  };
  Renderer.prototype.setView = function (view, immediate) {
    var fit = this.fitDistance(view === 'edge' ? 1.42 : 1.18);
    var views = {
      front: { x: -.02, y: -.055, distance: fit },
      surface: { x: -.32, y: -.45, distance: Math.max(2.1, fit * .52) },
      edge: { x: -.1, y: -1.0, distance: fit },
      back: { x: -.06, y: Math.PI - .06, distance: fit },
      reset: { x: -.05, y: -.11, distance: fit }
    };
    var next = views[view] || views.reset;
    var turns = Math.round((this.current.y - next.y) / (Math.PI * 2));
    next.y += turns * Math.PI * 2;
    this.target = { x: next.x, y: next.y, distance: next.distance };
    if (immediate) this.current = { x: next.x, y: next.y, distance: next.distance };
    this.requestRender();
  };
  Renderer.prototype.dispose = function () {
    cancelAnimationFrame(this.raf);
    this.webgl.domElement.removeEventListener('webglcontextlost', this.contextLost, false);
    this.webgl.dispose();
    if (this.webgl.forceContextLoss) this.webgl.forceContextLoss();
  };

  function InteractionController(viewer) {
    this.viewer = viewer;
    this.pointers = new Map();
    this.dragging = false;
    this.lastDistance = 0;
    this.bindQuiet();
  }
  InteractionController.prototype.bindQuiet = function () {
    var self = this, mount = this.viewer.quietMount;
    if (!this.viewer.config.quietTilt || this.viewer.performance.reducedMotion) return;
    this.quietMove = function (event) {
      if (self.viewer.fullOpen || event.pointerType === 'touch') return;
      var rect = mount.getBoundingClientRect();
      var nx = ((event.clientX - rect.left) / rect.width - .5) * 2;
      var ny = ((event.clientY - rect.top) / rect.height - .5) * 2;
      var radians = self.viewer.config.maxTilt * Math.PI / 180;
      self.viewer.renderer.target.x = -ny * radians;
      self.viewer.renderer.target.y = nx * radians;
      self.viewer.renderer.requestRender();
    };
    this.quietLeave = function () { self.viewer.renderer.setView('front'); };
    mount.addEventListener('pointermove', this.quietMove, { passive: true });
    mount.addEventListener('pointerleave', this.quietLeave, { passive: true });
  };
  InteractionController.prototype.bindFull = function (element) {
    var self = this;
    this.fullElement = element;
    this.pointerDown = function (event) {
      element.setPointerCapture(event.pointerId);
      self.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      self.dragging = true;
      self.lastDistance = 0;
      self.viewer.dismissHint();
    };
    this.pointerMove = function (event) {
      if (!self.pointers.has(event.pointerId)) return;
      var previous = self.pointers.get(event.pointerId);
      self.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      var points = Array.from(self.pointers.values());
      if (points.length === 1) {
        self.viewer.renderer.target.y += (event.clientX - previous.x) * .009;
        self.viewer.renderer.target.x = clamp(self.viewer.renderer.target.x + (event.clientY - previous.y) * .007, -1.35, 1.35);
      } else if (points.length === 2) {
        var distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        if (self.lastDistance) self.viewer.zoom((distance - self.lastDistance) * .012);
        self.lastDistance = distance;
      }
      self.viewer.renderer.requestRender();
    };
    this.pointerUp = function (event) { self.pointers.delete(event.pointerId); self.dragging = self.pointers.size > 0; self.lastDistance = 0; };
    this.wheel = function (event) { event.preventDefault(); self.viewer.zoom(-event.deltaY * .003); };
    element.addEventListener('pointerdown', this.pointerDown);
    element.addEventListener('pointermove', this.pointerMove);
    element.addEventListener('pointerup', this.pointerUp);
    element.addEventListener('pointercancel', this.pointerUp);
    element.addEventListener('wheel', this.wheel, { passive: false });
  };
  InteractionController.prototype.dispose = function () {
    if (this.quietMove) this.viewer.quietMount.removeEventListener('pointermove', this.quietMove);
    if (this.quietLeave) this.viewer.quietMount.removeEventListener('pointerleave', this.quietLeave);
    if (this.fullElement) {
      this.fullElement.removeEventListener('pointerdown', this.pointerDown);
      this.fullElement.removeEventListener('pointermove', this.pointerMove);
      this.fullElement.removeEventListener('pointerup', this.pointerUp);
      this.fullElement.removeEventListener('pointercancel', this.pointerUp);
      this.fullElement.removeEventListener('wheel', this.wheel);
    }
  };

  function RealityViewer(root) {
    this.root = root;
    this.quietMount = root.querySelector('[data-ca-reality-mount]');
    this.openButton = root.querySelector('[data-ca-reality-open]');
    this.status = root.querySelector('[data-ca-reality-status]');
    this.ratioHelp = root.querySelector('[data-ca-reality-ratio-help]');
    this.modal = document.querySelector('[data-ca-reality-modal="' + root.dataset.sectionId + '"]');
    this.fullMount = this.modal && this.modal.querySelector('[data-ca-reality-full-mount]');
    if (this.modal && this.modal.parentElement !== document.body) document.body.appendChild(this.modal);
    this.resolver = new ArtworkResolver(root);
    this.performance = new PerformanceManager();
    this.config = {
      depthIn: (Number(root.dataset.canvasDepthMm) || 38) / 25.4,
      maxTilt: clamp(Number(root.dataset.maxQuietTilt) || 3, 0, 4),
      quietTilt: bool(root.dataset.quietTilt),
      highResolution: bool(root.dataset.highResolution),
      hardware: root.dataset.hardware
    };
    this.initialized = false;
    this.fullOpen = false;
    this.currentSelection = null;
    this.adapter = new ProductDataAdapter(root);
    this.adapter.connect(this.selectionChanged.bind(this));
    if (!hasWebGL() || !this.resolver.normalUrl) return this.fail('Interactive 3D is unavailable. The standard artwork preview remains active.');
    this.openButton.hidden = !this.currentSelection || !FORMAT_REGISTRY[this.currentSelection.format];
    this.bindUI();
    this.observe();
  }
  RealityViewer.prototype.observe = function () {
    var self = this;
    this.visibilityChange = function () {
      if (!self.renderer) return;
      self.renderer.visible = !document.hidden && (self.isIntersecting || self.fullOpen);
      if (self.renderer.visible) self.renderer.requestRender();
    };
    document.addEventListener('visibilitychange', this.visibilityChange);
    if (!('IntersectionObserver' in window)) return this.maybeInitialize(false);
    this.observer = new IntersectionObserver(function (entries) {
      self.isIntersecting = entries.some(function (entry) { return entry.isIntersecting; });
      if (self.renderer) {
        self.renderer.visible = self.isIntersecting || self.fullOpen;
        if (self.renderer.visible) self.renderer.requestRender();
        else { cancelAnimationFrame(self.renderer.raf); self.renderer.raf = 0; }
      }
      if (self.isIntersecting) self.maybeInitialize(false);
    }, { rootMargin: '180px' });
    this.observer.observe(this.root);
  };
  RealityViewer.prototype.selectionChanged = function (selection) {
    this.currentSelection = selection;
    var physical3dSelected = selection.format === 'canvas' || selection.format === 'framed' || selection.format === 'acrylic';
    var ratioCompatible = !!(selection.dimensions && Math.abs(selection.dimensions.ratio / this.resolver.aspect - 1) <= RATIO_TOLERANCE);
    var supported = physical3dSelected && !!FORMAT_REGISTRY[selection.format] && !!selection.dimensions;
    if (physical3dSelected && selection.dimensions && !ratioCompatible) {
      this.root.dataset.realityReason = 'dimension-conflict';
      if (this.status) this.status.textContent = 'The selected size and artwork use different proportions. Open size details for production guidance.';
    } else this.root.dataset.realityReason = supported ? 'eligible' : 'format-ineligible';
    if (this.ratioHelp) this.ratioHelp.hidden = !(physical3dSelected && selection.dimensions && !ratioCompatible);
    var hasFormatRadios = !!document.querySelector('[data-ptv]');
    if (selection.format === 'canvas' && !hasFormatRadios) {
      var canvasStage = document.getElementById('sp-stage-canvas');
      var framedStage = document.getElementById('sp-stage-framed');
      var acrylicStage = document.getElementById('sp-stage-acrylic');
      if (canvasStage) canvasStage.style.display = 'flex';
      if (framedStage) framedStage.style.display = 'none';
      if (acrylicStage) acrylicStage.style.display = 'none';
    }
    var artPanel = document.getElementById('sp-artpanel');
    if (artPanel) {
      artPanel.setAttribute('data-reality-canvas', supported ? 'true' : 'false');
      if (physical3dSelected && artPanel.classList.contains('is-observing')) {
        artPanel.classList.remove('is-observing');
        var observe = document.getElementById('sp-observe');
        if (observe) observe.setAttribute('aria-pressed', 'false');
      }
    }
    this.root.classList.toggle('is-selected', supported);
    this.openButton.hidden = !supported;
    var selectionLabel = this.modal && this.modal.querySelector('[data-ca-reality-selection]');
    var formatLabel = this.modal && this.modal.querySelector('[data-ca-reality-format]');
    if (selectionLabel) {
      var formatLabels = { canvas:'Gallery Canvas', framed:'Framed Fine Art Print', acrylic:'Acrylic Glass' };
      selectionLabel.textContent = (formatLabels[selection.format] || 'Physical artwork') + (selection.dimensions ? ' / ' + selection.dimensions.raw : ' / Choose a size');
      if (formatLabel) formatLabel.textContent = (formatLabels[selection.format] || 'Physical artwork') + ' / Construction preview';
    }
    if (!supported && this.initializing) {
      this.generation = (this.generation || 0) + 1;
      this.initializing = false;
      if (this.textureManager) { this.textureManager.dispose(); this.textureManager = null; }
      if (this.format) { this.format.dispose(); this.format = null; }
      if (this.renderer) { this.renderer.dispose(); this.renderer = null; }
    }
    if (!supported && this.fullOpen) this.close();
    if (supported) this.maybeInitialize(false);
    if (supported && this.initialized && this.format) {
      if (this.formatKey !== selection.format) this.activateFormat(selection.format, selection.dimensions);
      else this.format.updateDimensions(selection.dimensions);
      this.renderer.requestRender();
    }
  };
  RealityViewer.prototype.maybeInitialize = function (intentional) {
    var self = this;
    if (this.initialized || this.initializing || !this.currentSelection || !FORMAT_REGISTRY[this.currentSelection.format]) return;
    if (!intentional && (this.performance.constrained || this.performance.reducedMotion)) return;
    this.initializing = true;
    this.root.classList.remove('is-failed');
    var generation = this.generation = (this.generation || 0) + 1;
    loadThree(this.root.dataset.threeUrl).then(function (THREE) {
      if (generation !== self.generation) throw new Error('Stale initialization');
      self.renderer = new Renderer(THREE, self.performance);
      self.renderer.onContextLost = function () { self.fail('3D context lost. The standard artwork preview remains active.'); };
      self.textureManager = new TextureManager(THREE, self.renderer.webgl);
      var FormatConstructor = FORMAT_REGISTRY[self.currentSelection.format];
      self.formatKey = self.currentSelection.format;
      self.format = new FormatConstructor(THREE, {
        aspect: self.resolver.aspect, depthIn: self.config.depthIn, hardware: self.config.hardware
      });
      self.format.updateDimensions(self.currentSelection.dimensions);
      self.renderer.setFormat(self.format);
      self.renderer.mount(self.fullOpen ? self.fullMount : self.quietMount);
      self.renderer.setView(self.fullOpen ? 'reset' : 'front', true);
      self.interactions = new InteractionController(self);
      self.interactions.bindFull(self.fullMount);
      if ('ResizeObserver' in window) {
        self.resizeObserver = new ResizeObserver(function () { self.renderer.resize(); });
        self.resizeObserver.observe(self.quietMount);
        self.resizeObserver.observe(self.fullMount);
      } else {
        self.onWindowResize = function () { self.renderer.resize(); };
        window.addEventListener('resize', self.onWindowResize, { passive: true });
      }
      return self.textureManager.load(self.resolver.normalUrl);
    }).then(function (texture) {
      if (generation !== self.generation) throw new Error('Stale initialization');
      self.format.applyArtwork(texture);
      self.renderer.requestRender();
      self.initialized = true;
      self.initializing = false;
      self.root.classList.add('is-ready');
      self.removeModalFallback();
      if (self.status) self.status.textContent = 'Interactive 3D view ready.';
      emit('reality_viewer_ready', self.analyticsDetail());
      if (self.fullOpen) self.upgradeTexture();
    }).catch(function () { if (generation === self.generation) self.fail('Interactive 3D could not load. The standard artwork preview remains active.'); });
  };
  RealityViewer.prototype.analyticsDetail = function () {
    return { product_id: this.root.dataset.productId, product_handle: this.root.dataset.productHandle, format: this.formatKey || (this.currentSelection && this.currentSelection.format) || 'canvas', artwork_source: this.resolver.source };
  };
  RealityViewer.prototype.activateFormat = function (formatKey, dimensions) {
    var FormatConstructor = FORMAT_REGISTRY[formatKey];
    if (!FormatConstructor || !this.renderer) return false;
    if (this.format) this.format.dispose();
    this.formatKey = formatKey;
    this.format = new FormatConstructor(this.renderer.THREE, {
      aspect: this.resolver.aspect, depthIn: this.config.depthIn, hardware: this.config.hardware
    });
    this.format.updateDimensions(dimensions);
    if (this.textureManager && this.textureManager.texture) this.format.applyArtwork(this.textureManager.texture);
    this.renderer.setFormat(this.format);
    return true;
  };
  RealityViewer.prototype.addModalFallback = function () {
    if (this.fullMount.querySelector('.ca-reality__modal-fallback')) return;
    var image = document.createElement('img');
    image.className = 'ca-reality__modal-fallback';
    image.src = this.resolver.normalUrl;
    image.alt = '';
    image.style.cssText = 'position:absolute;inset:8%;width:84%;height:84%;object-fit:contain;filter:drop-shadow(0 24px 28px rgba(0,0,0,.2));';
    this.fullMount.appendChild(image);
  };
  RealityViewer.prototype.removeModalFallback = function () {
    var image = this.fullMount.querySelector('.ca-reality__modal-fallback');
    if (image) image.remove();
  };
  RealityViewer.prototype.upgradeTexture = function () {
    if (!this.config.highResolution || !this.textureManager || this.resolver.highUrl === this.resolver.normalUrl) return;
    var self = this;
    this.textureManager.load(this.resolver.highUrl).then(function (texture) {
      if (!self.fullOpen || !self.format || !self.renderer) return;
      self.format.applyArtwork(texture);
      self.renderer.requestRender();
    }).catch(function () {});
  };
  RealityViewer.prototype.lockPage = function () {
    this.hadScrollLock = document.documentElement.classList.contains('ca-reality-scroll-lock');
    document.documentElement.classList.add('ca-reality-scroll-lock');
    this.inerted = [];
    var modal = this.modal;
    Array.prototype.forEach.call(document.body.children, function (node) {
      if (node === modal || node.contains(modal)) return;
      this.inerted.push({ node: node, had: node.hasAttribute('inert') });
      node.setAttribute('inert', '');
    }, this);
  };
  RealityViewer.prototype.unlockPage = function () {
    if (!this.hadScrollLock) document.documentElement.classList.remove('ca-reality-scroll-lock');
    (this.inerted || []).forEach(function (item) { if (!item.had) item.node.removeAttribute('inert'); });
    this.inerted = [];
  };
  RealityViewer.prototype.bindUI = function () {
    var self = this;
    this.closeButton = this.modal.querySelector('[data-ca-reality-close]');
    this.openButton.addEventListener('click', function () { self.open(); });
    this.closeButton.addEventListener('click', function () { self.close(); });
    this.modal.addEventListener('click', function (event) { if (event.target === self.modal) self.close(); });
    this.modal.querySelectorAll('[data-ca-reality-preset]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (!self.renderer) return;
        var preset = button.dataset.caRealityPreset;
        self.renderer.setView(preset);
        self.markPreset(button);
        emit('reality_view_' + preset, self.analyticsDetail());
      });
    });
    this.modal.querySelectorAll('[data-ca-reality-zoom]').forEach(function (button) {
      button.addEventListener('click', function () { self.zoom(button.dataset.caRealityZoom === 'in' ? .35 : -.35); });
    });
    this.keydown = function (event) {
      if (!self.fullOpen) return;
      if (event.key === 'Escape') self.close();
      if (event.key === 'Tab') self.trapFocus(event);
      if (event.key === '+' || event.key === '=') self.zoom(.3);
      if (event.key === '-') self.zoom(-.3);
      if (event.key.indexOf('Arrow') === 0 && self.renderer) {
        event.preventDefault();
        if (event.key === 'ArrowLeft') self.renderer.target.y -= .12;
        if (event.key === 'ArrowRight') self.renderer.target.y += .12;
        if (event.key === 'ArrowUp') self.renderer.target.x = clamp(self.renderer.target.x - .1, -1.35, 1.35);
        if (event.key === 'ArrowDown') self.renderer.target.x = clamp(self.renderer.target.x + .1, -1.35, 1.35);
        self.renderer.requestRender();
      }
    };
    document.addEventListener('keydown', this.keydown);
  };
  RealityViewer.prototype.open = function () {
    if (!this.currentSelection || !this.currentSelection.dimensions || Math.abs(this.currentSelection.dimensions.ratio / this.resolver.aspect - 1) > RATIO_TOLERANCE) return;
    this.fullOpen = true;
    this.lastFocus = document.activeElement;
    this.scrollY = window.scrollY;
    this.modal.hidden = false;
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    this.lockPage();
    this.addModalFallback();
    if (this.initialized) {
      this.renderer.visible = true;
      this.renderer.mount(this.fullMount);
      this.renderer.setView('reset');
      this.format.setQuality('high');
      this.upgradeTexture();
      this.removeModalFallback();
    } else this.maybeInitialize(true);
    this.closeButton.focus();
    emit('reality_viewer_open', this.analyticsDetail());
  };
  RealityViewer.prototype.close = function () {
    if (!this.fullOpen) return;
    this.fullOpen = false;
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.hidden = true;
    this.unlockPage();
    window.scrollTo(0, this.scrollY || 0);
    if (this.initialized) {
      this.renderer.mount(this.quietMount);
      this.renderer.visible = this.isIntersecting !== false;
      this.renderer.setView('front');
      this.format.setQuality('normal');
    }
    this.removeModalFallback();
    (this.lastFocus || this.openButton).focus();
    emit('reality_viewer_close', this.analyticsDetail());
  };
  RealityViewer.prototype.zoom = function (delta) {
    if (!this.renderer) return;
    var fit = this.renderer.fitDistance ? this.renderer.fitDistance(1.18) : 5.8;
    this.renderer.target.distance = clamp(this.renderer.target.distance - delta, Math.max(1.6, fit * .42), fit * 1.8);
    this.renderer.requestRender();
    var now = Date.now();
    if (!this.lastZoomEvent || now - this.lastZoomEvent > 500) {
      this.lastZoomEvent = now;
      emit('reality_view_zoom', Object.assign(this.analyticsDetail(), { distance: Number(this.renderer.target.distance.toFixed(2)) }));
    }
  };
  RealityViewer.prototype.dismissHint = function () {
    var hint = this.modal.querySelector('[data-ca-reality-hint]');
    if (hint) hint.classList.add('is-dismissed');
  };
  RealityViewer.prototype.markPreset = function (active) {
    this.modal.querySelectorAll('[data-ca-reality-preset]').forEach(function (button) { button.setAttribute('aria-pressed', button === active ? 'true' : 'false'); });
  };
  RealityViewer.prototype.trapFocus = function (event) {
    var focusable = Array.prototype.slice.call(this.modal.querySelectorAll('button,[tabindex="0"]')).filter(function (node) { return !node.disabled && node.offsetParent !== null; });
    if (!focusable.length) return;
    var first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  RealityViewer.prototype.fail = function (message) {
    this.generation = (this.generation || 0) + 1;
    this.initializing = false;
    this.root.classList.add('is-failed');
    this.root.classList.remove('is-ready');
    this.openButton.hidden = true;
    if (this.status) this.status.textContent = message;
    if (this.fullOpen) this.close();
    if (this.textureManager) { this.textureManager.dispose(); this.textureManager = null; }
    if (this.format) { this.format.dispose(); this.format = null; }
    if (this.renderer) { this.renderer.dispose(); this.renderer = null; }
  };
  RealityViewer.prototype.dispose = function () {
    this.generation = (this.generation || 0) + 1;
    if (this.observer) this.observer.disconnect();
    if (this.visibilityChange) document.removeEventListener('visibilitychange', this.visibilityChange);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
    if (this.adapter) this.adapter.dispose();
    if (this.interactions) this.interactions.dispose();
    if (this.textureManager) this.textureManager.dispose();
    if (this.format) this.format.dispose();
    if (this.renderer) this.renderer.dispose();
    if (this.keydown) document.removeEventListener('keydown', this.keydown);
    if (this.modal && this.modal.parentElement === document.body) this.modal.remove();
  };

  window.CARealityEngine = window.CARealityEngine || {
    formats: FORMAT_REGISTRY,
    parseDimensions: parseDimensions,
    registerFormat: function (name, constructor) { FORMAT_REGISTRY[name] = constructor; }
  };

  function boot(scope) {
    (scope || document).querySelectorAll('[data-ca-reality][data-enabled="true"]').forEach(function (root) {
      if (!root.caRealityViewer) root.caRealityViewer = new RealityViewer(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-ca-reality]').forEach(function (root) { if (root.caRealityViewer) root.caRealityViewer.dispose(); });
  });
})();
